import React, {useCallback, useEffect, useRef, useState} from 'react';
import {SearchOutlined} from '@ant-design/icons';
import {Button, ConfigProvider, Input, Space, Table, theme} from 'antd';
import Highlighter from 'react-highlight-words';

export const Datatable = ({columns, rows, customRowClick, expandedRowRender = undefined}) => {
    const [data, setData] = useState(rows);
    const [expandedRowKeys, setExpandedRowKeys] = useState([]);

    const handleRowClick = customRowClick ? customRowClick : () => {};

    useEffect(() => {
        setData(rows);
    }, [rows]);

    const [tableParams, setTableParams] = useState({
        pagination: {
            current: 1,
            pageSize: 20,
            pageSizeOptions: [20, 50, 100],
            position: ['bottomRight'],
            showSizeChanger: true
        },
    });

    const [searchText, setSearchText] = useState('');
    const [searchedColumn, setSearchedColumn] = useState('');

    const searchInput = useRef(null);

    const handleExpandedRowsChange = (newExpandedRowKeys) => {
        // 새로운 행을 열 때, 기존 열려있던 행을 닫음
        if (newExpandedRowKeys.length > 0) {
            setExpandedRowKeys([newExpandedRowKeys[newExpandedRowKeys.length - 1]]);
        } else {
            setExpandedRowKeys([]);
        }
    };

    const handleSearch = (selectedKeys, confirm, dataIndex) => {
        confirm();
        setSearchText(selectedKeys[0]);
        setSearchedColumn(dataIndex);
    };

    const handleReset = (clearFilters) => {
        clearFilters();
        setSearchText('');
    };

    const getColumnSearchProps = useCallback((dataIndex) => ({
        filterDropdown: ({setSelectedKeys, selectedKeys, confirm, clearFilters, close}) => (
            <div
                className='p-2 border border-gray-800 rounded-md bg-gray-900/60'
                onKeyDown={(e) => e.stopPropagation()}
            >
                <Input
                    ref={searchInput}
                    placeholder={`Search "${dataIndex}"`}
                    value={selectedKeys[0]}
                    onChange={(e) => setSelectedKeys(e.target.value ? [e.target.value] : [])}
                    onPressEnter={() => handleSearch(selectedKeys, confirm, dataIndex)}
                    style={{
                        marginBottom: 8,
                        display: 'block',
                    }}
                    className='h-8 py-0 text-sm border-0 input ring-1'
                />
                <Space>
                    <Button
                        onClick={() => handleSearch(selectedKeys, confirm, dataIndex)}
                        icon={<SearchOutlined/>}
                        size="small"
                        style={{
                            width: 90,
                        }}
                    >
                        Search
                    </Button>
                    <Button
                        onClick={() => clearFilters && handleReset(clearFilters)}
                        size="small"
                        style={{
                            width: 90,
                        }}
                    >
                        Clear
                    </Button>
                    <Button
                        type="link"
                        size="small"
                        onClick={() => {
                            confirm({
                                closeDropdown: false,
                            });
                            setSearchText(selectedKeys[0]);
                            setSearchedColumn(dataIndex);
                        }}
                    >
                        Filter
                    </Button>
                    <Button
                        type="link"
                        size="small"
                        onClick={() => {
                            close();
                        }}
                    >
                        close
                    </Button>
                </Space>
            </div>
        ),
        filterIcon: (filtered) => (
            <SearchOutlined
                style={{
                    color: filtered ? '#1677ff' : undefined,
                }}
            />
        ),
        onFilter: (value, record) =>
            record[dataIndex].toString().toLowerCase().includes(value.toLowerCase()),
        filterDropdownProps: {
            onOpenChange: (visible) => {
                if (visible) {
                    setTimeout(() => searchInput.current?.select(), 100);
                }
            },
        },
        render: (text) =>
            searchedColumn === dataIndex ? (
                <Highlighter
                    highlightStyle={{
                        backgroundColor: '#ffc069',
                        padding: 0,
                    }}
                    searchWords={[searchText]}
                    autoEscape
                    textToHighlight={text ? text.toString() : ''}
                />
            ) : (
                text
            ),
    }),[searchedColumn, searchText]);

    const handleTableChange = (pagination, filters, sorter) => {
        setTableParams({
            pagination,
            filters,
            ...sorter,
        });
    };

    const columns_dt = columns.map((column) => {
        column.align = 'center';
        if (column.search) {
            return {...column, ...getColumnSearchProps(column.dataIndex)};
        } else {
            return column;
        }
    });

    return (
        <ConfigProvider theme={{algorithm: theme.compactAlgorithm}}>
            <Table
                columns={columns_dt}
                dataSource={data}
                pagination={tableParams.pagination}
                onChange={handleTableChange}
                style={{overflowX: "auto"}}
                rowKey={(record) => record.key}
                expandable={{
                    expandedRowRender,
                    expandedRowKeys,
                    onExpandedRowsChange: handleExpandedRowsChange,
                }}
                onRow={(record) => {
                    return {
                        onClick: () => {
                            handleRowClick(record);
                        },
                    };
                }}
                size={'small'}
            />
        </ConfigProvider>
    );
}